import { useState, useCallback, useRef, useEffect } from 'react';
import RecordRTC from 'recordrtc';
import { Auth } from 'aws-amplify';

// Debug: Check if Auth is properly imported
console.log('🔍 Auth import check:', typeof Auth, !!Auth.currentSession);

/**
 * Media Server Recording Hook
 * Integrates with your existing Interview component to stream video to media server
 * instead of uploading directly to S3
 * 
 * LATEST IMPROVEMENTS (Sept 2025):
 * -  MP4 format with H.264/AAC codecs for maximum compatibility (fallback to WebM)
 * -  2-second chunks instead of 5-second for better data integrity  
 * -  Enhanced chunk validation (size, MIME type, corruption detection)
 * -  Reduced bitrate (2.5Mbps video) for stable chunk generation
 * -  Comprehensive logging for debugging chunk corruption issues
 * -  ID token authentication for S3 upload permissions
 * 
 * This addresses the 86% chunk corruption issue using more reliable formats
 * and smaller chunk sizes that are easier for MediaRecorder to handle properly.
 */

// ========================================
// MEDIA SERVER CONFIGURATION
// ========================================
// Easy configuration for development - change these values as needed:

const MEDIA_SERVER_CONFIG = {
  enabled: true, // Set to false to disable media server and use direct S3 upload
  url: 'wss://phone.getscreened.in/media_server/ws', // Your media server WebSocket URL with proper S3 authentication
  maxRetries: 3,
  retryDelay: 1000,
  // Enhanced chunk configuration - smaller intervals for more frequent chunks
  chunkMode: 'timed', // 'timed' for timeSlice chunks, 'auto' for automatic chunking
  chunkDuration: 1000, // 1 second (reduced from 3 seconds for more frequent transmission)
  // Connection monitoring configuration
  heartbeat: {
    enabled: true,
    interval: 10000, // Send ping every 10 seconds
    timeout: 5000,   // Wait 5 seconds for pong response
    maxMissed: 3     // Reconnect after 3 missed heartbeats
  },
  // Buffer management configuration
  buffer: {
    maxSize: 2000,         // Maximum chunks to buffer (covers ~33 min at 1s chunks without eviction)
    retryAttempts: 3,      // Retry attempts per chunk
    retryDelay: 500,       // Base delay between retries (ms)
    acknowledgmentTimeout: 10000, // Wait 10s for chunk acknowledgment
    // Cap on chunks held in the "WS down / server not ready" pending buffer.
    // Prevents runaway memory growth if the connection stays broken for a
    // long time. At ~150KB/chunk × 2000 = ~300MB worst case.
    maxPendingChunks: 2000
  },
  // Connection stability monitoring
  reconnection: {
    enabled: true,
    maxAttempts: 5,
    baseDelay: 1000,       // Base reconnection delay
    maxDelay: 30000,       // Maximum reconnection delay
    exponentialBackoff: true
  },
  // Enhanced media format configuration with reduced bitrates
  mediaFormat: {
    // Primary format: MP4 (most reliable) - reduced bitrate
    mimeType: 'video/mp4; codecs="avc1.424028, mp4a.40.2"',
    // Fallback format: WebM with VP8/Opus - reduced bitrate
    fallbackMimeType: 'video/webm; codecs="vp8, opus"',
    videoBitsPerSecond: 1500000, // Reduced from 2.5MB to 1.5MB for better stability
    audioBitsPerSecond: 128000
  }
};

// ========================================

export const useMediaServerRecording = ({
  interviewId,
  candidateId,
  userId,
  sessionId,
  onRecordingComplete,
  onError,
  onProgress,
  isAITalking = false, // New prop to track when AI is speaking
  audioConstraints = null, // Audio constraints for microphone
  interviewType = null // 'jd_specific' | 'screening' — drives strict chunk ordering
}) => {
  // All interview types must dispatch chunks strictly in arrival order so
  // sequence numbers match wall-clock order. Without this, async validation
  // could let chunk B overtake chunk A in the queue, scrambling the server's
  // assembly and dropping middle chunks (the 30-min → 3-min recording bug).
  // Previously this was gated to interviewType === 'jd_specific', which left
  // screening interviews vulnerable to the same race.
  const strictChunkOrderingRef = useRef(true);
  // Connection states
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isServerReady, setIsServerReady] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [connectionStability, setConnectionStability] = useState('stable'); // 'stable', 'unstable', 'reconnecting'

  // References
  const wsRef = useRef(null);
  const recordRTCRef = useRef(null);
  const streamRef = useRef(null);
  const sequenceRef = useRef(0);
  const isServerReadyRef = useRef(false);
  const recordingStartTimeRef = useRef(null);
  const chunkCountRef = useRef(0);
  // Serializes chunk dispatch so metadata + binary for one chunk always go
  // contiguously over the WebSocket, in submission order.
  const sendQueueRef = useRef(Promise.resolve());
  // Chunks that arrived while the WebSocket was closed or the server was
  // not yet ready. They sit here in arrival order and get drained back into
  // sendQueueRef once the connection becomes usable again — replacing the
  // old behavior of silently dropping them.
  const pendingChunksRef = useRef([]);
  // MIME type chosen by MediaRecorder at start; reused for every chunk so we
  // don't mis-tag MP4 fragments whose blob.type is empty.
  const recorderMimeTypeRef = useRef(null);
  
  // Audio stream management refs
  const microphoneStreamRef = useRef(null);
  const screenStreamRef = useRef(null); // Store original screen stream
  const audioContextRef = useRef(null);
  const audioMixerRef = useRef(null);
  const combinedStreamRef = useRef(null);
  const screenAudioNodeRef = useRef(null);
  const microphoneAudioNodeRef = useRef(null);
  const destinationNodeRef = useRef(null);
  
  // Buffer management refs
  const chunkBufferRef = useRef(new Map()); // Map<chunkId, {blob, metadata, retryCount, timestamp}>
  const pendingAcksRef = useRef(new Set()); // Set of chunk IDs waiting for acknowledgment
  const retryQueueRef = useRef([]); // Array of chunk IDs to retry
  
  // Connection monitoring refs
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const missedHeartbeatsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastHeartbeatRef = useRef(null);
  const connectFunctionRef = useRef(null);

  // ========================================
  // ENHANCED MEDIARECORDER CONFIGURATION
  // ========================================

  // Create optimal MediaRecorder with enhanced codec priority and fallback system

  // Get browser-specific optimal settings with enhanced compatibility - PREFER MP4
  const getBrowserOptimalSettings = useCallback(() => {
    const isChrome = navigator.userAgent.includes('Chrome');
    const isFirefox = navigator.userAgent.includes('Firefox');
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    
    if (isChrome) {
      return {
        preferredMimeType: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"', // Chrome MP4 H.264
        chunkInterval: 1000,  // 1 second for Chrome (reduced for frequent transmission)
        videoBitrate: 1500000  // 1.5Mbps
      };
    } else if (isFirefox) {
      return {
        preferredMimeType: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"', // Firefox MP4 H.264
        chunkInterval: 1000,  // 1 second for Firefox
        videoBitrate: 1500000  // 1.5Mbps
      };
    } else if (isSafari) {
      return {
        preferredMimeType: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"', // Safari MP4 H.264
        chunkInterval: 1000,  // 1 second for Safari
        videoBitrate: 1500000  // 1.5Mbps
      };
    }
    
    // Default for other browsers
    return {
      preferredMimeType: 'video/mp4',
      chunkInterval: 1000,  // 1 second default
      videoBitrate: 1500000  // 1.5Mbps
    };
  }, []);

  // Create optimal MediaRecorder with enhanced codec priority and fallback system - PREFER MP4 FOR CHUNK STABILITY
  const createOptimalMediaRecorder = useCallback((stream) => {
    // Priority order: avc3 > avc1 > basic MP4 > WebM (MP4 first for better chunk stability).
    // avc3 is preferred when supported because it allows in-band SPS/PPS
    // parameter sets, making fragmented MP4 chunks more self-describing for
    // server-side assembly. Browsers that don't support avc3 in MediaRecorder
    // (most current builds) automatically fall through to avc1.
    const codecOptions = [
      {
        mimeType: 'video/mp4; codecs="avc3.42E01E,mp4a.40.2"',
        videoBitsPerSecond: 1500000,
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
        videoBitsPerSecond: 1500000,  // 1.5Mbps for MP4 H.264 (most stable for chunking)
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/mp4; codecs="avc1.424028,mp4a.40.2"',
        videoBitsPerSecond: 1500000,  // 1.5Mbps for MP4 H.264 (alternative profile)
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/mp4',
        videoBitsPerSecond: 1500000,  // 1.5Mbps for basic MP4
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/webm; codecs="vp8,opus"',
        videoBitsPerSecond: 1200000,  // 1.2Mbps for WebM VP8 (fallback)
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/webm; codecs="vp9,opus"',
        videoBitsPerSecond: 1000000,  // 1Mbps for WebM VP9 (fallback)
        audioBitsPerSecond: 128000
      },
      {
        mimeType: 'video/webm',
        videoBitsPerSecond: 800000,   // 800kbps for basic WebM (last resort)
        audioBitsPerSecond: 128000
      }
    ];

    for (const config of codecOptions) {
      if (MediaRecorder.isTypeSupported(config.mimeType)) {
        try {
          const recorder = new MediaRecorder(stream, config);
          console.log(`✅ Using optimal MediaRecorder: ${config.mimeType}`, {
            videoBitrate: config.videoBitsPerSecond,
            audioBitrate: config.audioBitsPerSecond,
            state: recorder.state,
            chunkingStability: config.mimeType.includes('webm') ? 'high' : 'moderate'
          });
          return { recorder, config };
        } catch (error) {
          console.warn(`❌ Failed to create recorder with ${config.mimeType}:`, error);
          continue;
        }
      } else {
        console.warn(`⚠️ Format not supported: ${config.mimeType}`);
      }
    }
    
    // Emergency fallback recorder
    console.warn('🔄 Creating emergency fallback recorder with minimal settings');
    return createFallbackRecorder(stream);
  }, []);

  // Emergency fallback recorder when all optimal configurations fail
  const createFallbackRecorder = useCallback((stream) => {
    console.warn('🔄 Creating fallback recorder with minimal settings');
    
    // Try the most basic WebM configuration
    const basicConfig = {
      mimeType: 'video/webm',
      // No bitrate constraints - let browser decide
    };
    
    if (MediaRecorder.isTypeSupported(basicConfig.mimeType)) {
      try {
        const recorder = new MediaRecorder(stream, basicConfig);
        console.log('✅ Fallback recorder created with basic WebM');
        return { recorder, config: { mimeType: 'video/webm', videoBitsPerSecond: 'auto' } };
      } catch (error) {
        console.error('❌ Basic WebM fallback failed:', error);
      }
    }
    
    // Last resort - no constraints at all
    try {
      const recorder = new MediaRecorder(stream);
      console.log('✅ Last resort recorder created with no constraints');
      return { recorder, config: { mimeType: 'auto', videoBitsPerSecond: 'auto' } };
    } catch (error) {
      console.error('❌ All MediaRecorder creation attempts failed:', error);
      throw new Error('No supported video formats found - browser may not support MediaRecorder API');
    }
  }, []);

  // Client-side chunk corruption validation with relaxed thresholds
  const validateChunkForCorruption = useCallback((chunkBlob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function(e) {
        const arrayBuffer = e.target.result;
        const bytes = new Uint8Array(arrayBuffer);
        
        // Check for corruption indicators but with relaxed threshold
        let ffCount = 0;  // 0xFF bytes
        let feCount = 0;  // 0xFE bytes
        
        // Sample first 100 bytes for corruption detection
        const sampleSize = Math.min(100, bytes.length);
        for (let i = 0; i < sampleSize; i++) {
          if (bytes[i] === 0xFF) ffCount++;
          if (bytes[i] === 0xFE) feCount++;
        }
        
        const corruptionRatio = (ffCount + feCount) / sampleSize;
        
        // Check for proper WebM/MP4 headers - VERY RELAXED
        const firstBytes = Array.from(bytes.slice(0, 8));
        const hasValidWebMHeader = bytes.length > 4 && 
          (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3); // EBML
        
        // More permissive MP4 header detection - check for common MP4 atoms
        const hasValidMP4Header = bytes.length > 8 && (
          // Standard ftyp signature
          (firstBytes.includes(0x66, 4) && firstBytes.includes(0x74, 5) && firstBytes.includes(0x79, 6)) ||
          // moof atom (movie fragment) - common in streaming MP4
          (bytes[4] === 0x6d && bytes[5] === 0x6f && bytes[6] === 0x6f && bytes[7] === 0x66) ||
          // mdat atom (media data)
          (bytes[4] === 0x6d && bytes[5] === 0x64 && bytes[6] === 0x61 && bytes[7] === 0x74) ||
          // Any MP4 box header pattern
          bytes.length > 16 // Accept any reasonable-sized chunk as potentially valid MP4
        );
        
        // RELAXED THRESHOLD: Increased from 10% to 50% to reduce false positives
        if (corruptionRatio > 0.5) {  // 50% threshold instead of 10%
          console.error(`🚨 Severe corruption detected: ${(corruptionRatio * 100).toFixed(1)}% invalid bytes (FF/FE pattern)`, {
            size: bytes.length,
            ffCount,
            feCount,
            corruptionRatio: (corruptionRatio * 100).toFixed(1) + '%',
            firstBytes: Array.from(bytes.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' '),
            hasValidWebMHeader,
            hasValidMP4Header,
            threshold: '50% (relaxed from 10%)'
          });
          resolve({ 
            valid: false, 
            reason: `Severe chunk corruption: ${(corruptionRatio * 100).toFixed(1)}% invalid bytes (above 50% threshold)`,
            corruptionRatio,
            ffCount,
            feCount
          });
        } else if (corruptionRatio > 0.1) {
          // Moderate corruption - warn but accept
          console.warn(`⚠️ Moderate corruption detected but accepting: ${(corruptionRatio * 100).toFixed(1)}% invalid bytes`, {
            size: bytes.length,
            ffCount,
            feCount,
            corruptionRatio: (corruptionRatio * 100).toFixed(1) + '%',
            firstBytes: Array.from(bytes.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' '),
            hasValidWebMHeader,
            hasValidMP4Header,
            acceptanceReason: 'Below 50% severe corruption threshold'
          });
          resolve({ 
            valid: true, 
            reason: `Moderate corruption accepted: ${(corruptionRatio * 100).toFixed(1)}% invalid bytes (below 50% threshold)`,
            corruptionRatio,
            ffCount,
            feCount,
            warning: true
          });
        } else if (!hasValidWebMHeader && !hasValidMP4Header && bytes.length > 10) {
          // Header validation - also more permissive
          console.warn(`⚠️ Unusual header detected but accepting chunk`, {
            size: bytes.length,
            firstBytes: Array.from(bytes.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '),
            expectedWebM: '1a 45 df a3 (EBML header)',
            expectedMP4: 'ftyp signature',
            acceptanceReason: 'Relaxed header validation'
          });
          resolve({ 
            valid: true, 
            reason: 'Unusual header accepted under relaxed validation',
            headerWarning: true,
            corruptionRatio
          });
        } else {
          console.log('✅ Chunk corruption validation passed', {
            size: bytes.length,
            corruptionRatio: (corruptionRatio * 100).toFixed(1) + '%',
            hasValidWebMHeader,
            hasValidMP4Header,
            firstBytes: Array.from(bytes.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ')
          });
          resolve({ 
            valid: true, 
            reason: 'Chunk validation passed - corruption below threshold',
            corruptionRatio
          });
        }
      };
      
      reader.onerror = function() {
        console.error('❌ Failed to read chunk for corruption validation');
        // On read error, accept the chunk rather than reject
        resolve({ valid: true, reason: 'Read error - chunk accepted by default' });
      };
      
      reader.readAsArrayBuffer(chunkBlob);
    });
  }, []);

  // ========================================
  // AUDIO STREAM MANAGEMENT FUNCTIONS
  // ========================================

  // Get microphone stream with candidate audio
  const getMicrophoneStream = useCallback(async () => {
    try {
      console.log('🎤 Requesting microphone access for candidate audio...');
      
      // Use provided audio constraints or default optimized constraints
      const micConstraints = audioConstraints || {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,
        channelCount: 1
      };
      
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: micConstraints,
        video: false
      });
      
      microphoneStreamRef.current = micStream;
      console.log('✅ Microphone stream obtained successfully');
      return micStream;
    } catch (error) {
      console.error('❌ Failed to get microphone stream:', error);
      throw new Error(`Microphone access failed: ${error.message}`);
    }
  }, [audioConstraints]);

  // Create audio context and mixer for combining streams
  const createAudioMixer = useCallback(async (screenStream, microphoneStream) => {
    try {
      console.log('🎛️ Creating audio context and mixer...');
      console.log('🔍 Screen stream audio tracks:', screenStream.getAudioTracks().length);
      console.log('🔍 Microphone stream audio tracks:', microphoneStream.getAudioTracks().length);
      
      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Create source nodes
      const screenAudioNode = audioContext.createMediaStreamSource(screenStream);
      const microphoneAudioNode = audioContext.createMediaStreamSource(microphoneStream);
      
      screenAudioNodeRef.current = screenAudioNode;
      microphoneAudioNodeRef.current = microphoneAudioNode;
      
      // Create destination node (output)
      const destination = audioContext.createMediaStreamDestination();
      destinationNodeRef.current = destination;
      
      // Create gain nodes for volume control
      const screenGain = audioContext.createGain();
      const microphoneGain = audioContext.createGain();
      
      // Set initial gains
      screenGain.gain.setValueAtTime(1.0, audioContext.currentTime); // Always include screen audio
      microphoneGain.gain.setValueAtTime(0.0, audioContext.currentTime); // Start with muted microphone
      
      console.log('🔧 Initial audio gains set: Screen=1.0, Microphone=0.0 (will be controlled by AI state)');
      
      // Connect audio graph
      screenAudioNode.connect(screenGain);
      microphoneAudioNode.connect(microphoneGain);
      
      screenGain.connect(destination);
      microphoneGain.connect(destination);
      
      // Store gain nodes for dynamic control
      audioMixerRef.current = {
        audioContext,
        screenGain,
        microphoneGain,
        destination,
        screenAudioNode,
        microphoneAudioNode
      };
      
      console.log('✅ Audio mixer created successfully');
      console.log('🎯 Mixed stream will dynamically include/exclude microphone based on AI speaking state');
      return destination.stream;
    } catch (error) {
      console.error('❌ Failed to create audio mixer:', error);
      throw new Error(`Audio mixer creation failed: ${error.message}`);
    }
  }, []);

  // Update microphone audio based on AI speaking state
  const updateMicrophoneAudio = useCallback((shouldIncludeMic) => {
    if (!audioMixerRef.current) {
      // Silently handle missing audio mixer - this is expected in some configurations
      return;
    }
    
    const { microphoneGain, screenGain, audioContext } = audioMixerRef.current;
    
    if (shouldIncludeMic) {
      // Include candidate microphone audio
      microphoneGain.gain.setValueAtTime(1.0, audioContext.currentTime);
      // Reduce logging frequency for microphone state changes
    } else {
      // Mute microphone audio completely (AI is speaking)
      microphoneGain.gain.setValueAtTime(0.0, audioContext.currentTime);
      // Reduce logging frequency for microphone state changes
    }
  }, []);

  // Clean up audio streams and context
  const cleanupAudioStreams = useCallback(() => {
    console.log('🧹 Cleaning up audio streams and context...');
    
    // Stop microphone stream
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🛑 Stopped microphone track:', track.kind);
      });
      microphoneStreamRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().then(() => {
        console.log('✅ Audio context closed');
      }).catch(error => {
        console.warn('⚠️ Error closing audio context:', error);
      });
      audioContextRef.current = null;
    }
    
    // Clear refs
    audioMixerRef.current = null;
    combinedStreamRef.current = null;
    screenAudioNodeRef.current = null;
    microphoneAudioNodeRef.current = null;
    destinationNodeRef.current = null;
    
    console.log('✅ Audio cleanup completed');
  }, []);

  // Effect to update microphone audio when AI speaking state changes
  useEffect(() => {
    // Add immediate response to AI speaking state changes
    const updateImmediately = () => {
      updateMicrophoneAudio(!isAITalking);
    };
    
    // Update immediately
    updateImmediately();
    
    // Also set a small delay to ensure the state change propagates
    const timeoutId = setTimeout(updateImmediately, 50);
    
    return () => clearTimeout(timeoutId);
  }, [isAITalking, updateMicrophoneAudio]);

  // ========================================
  // BUFFER MANAGEMENT FUNCTIONS
  // ========================================

  // Add chunk to buffer with retry capability
  const addChunkToBuffer = useCallback((chunkId, blob, metadata) => {
    const chunkData = {
      blob,
      metadata,
      retryCount: 0,
      timestamp: Date.now(),
      acknowledged: false
    };
    
    chunkBufferRef.current.set(chunkId, chunkData);
    pendingAcksRef.current.add(chunkId);

    // Enforce buffer size limit — but ONLY evict chunks the server has
    // already acknowledged. Evicting un-acked chunks lost the retry path
    // and silently dropped the middle of long recordings. If every chunk
    // in the buffer is un-acked we let it grow and log loudly rather than
    // sacrificing recording integrity to a memory cap.
    if (chunkBufferRef.current.size > MEDIA_SERVER_CONFIG.buffer.maxSize) {
      let evictedId = null;
      for (const [id, chunk] of chunkBufferRef.current) {
        if (chunk.acknowledged) {
          evictedId = id;
          break;
        }
      }
      if (evictedId !== null) {
        console.warn(`⚠️ Buffer overflow, removing acknowledged chunk: ${evictedId}`);
        removeChunkFromBuffer(evictedId);
      } else {
        console.error(`🚨 Buffer at ${chunkBufferRef.current.size}/${MEDIA_SERVER_CONFIG.buffer.maxSize} but no acknowledged chunks to evict. Keeping all un-acked chunks to preserve recording integrity — server may be slow/silent on acks.`);
      }
    }
    
    console.log(`📦 Added chunk ${chunkId} to buffer (${chunkBufferRef.current.size}/${MEDIA_SERVER_CONFIG.buffer.maxSize})`);
  }, []);

  // Remove chunk from buffer
  const removeChunkFromBuffer = useCallback((chunkId) => {
    chunkBufferRef.current.delete(chunkId);
    pendingAcksRef.current.delete(chunkId);
    retryQueueRef.current = retryQueueRef.current.filter(id => id !== chunkId);
    console.log(`🗑️ Removed chunk ${chunkId} from buffer`);
  }, []);

  // Mark chunk as acknowledged
  const acknowledgeChunk = useCallback((chunkId) => {
    const chunk = chunkBufferRef.current.get(chunkId);
    if (chunk) {
      chunk.acknowledged = true;
      pendingAcksRef.current.delete(chunkId);
      console.log(`✅ Chunk ${chunkId} acknowledged by server`);
      
      // Clean up acknowledged chunk after a delay
      setTimeout(() => removeChunkFromBuffer(chunkId), 5000);
    }
  }, [removeChunkFromBuffer]);

  // Retry failed chunks with exponential backoff
  const retryFailedChunks = useCallback(async () => {
    if (retryQueueRef.current.length === 0) return;
    
    console.log(`🔄 Retrying ${retryQueueRef.current.length} failed chunks`);
    
    for (const chunkId of [...retryQueueRef.current]) {
      const chunk = chunkBufferRef.current.get(chunkId);
      if (!chunk || chunk.acknowledged) {
        retryQueueRef.current = retryQueueRef.current.filter(id => id !== chunkId);
        continue;
      }
      
      if (chunk.retryCount >= MEDIA_SERVER_CONFIG.buffer.retryAttempts) {
        console.error(`❌ Chunk ${chunkId} exceeded max retry attempts, dropping`);
        removeChunkFromBuffer(chunkId);
        continue;
      }
      
      chunk.retryCount++;
      const delay = MEDIA_SERVER_CONFIG.buffer.retryDelay * Math.pow(2, chunk.retryCount - 1);
      
      console.log(`🔄 Retrying chunk ${chunkId} (attempt ${chunk.retryCount}/${MEDIA_SERVER_CONFIG.buffer.retryAttempts}) after ${delay}ms`);
      
      setTimeout(async () => {
        try {
          if (strictChunkOrderingRef.current) {
            // Funnel retries through the same serial queue so a retry can't
            // overtake a fresh chunk mid-flight under JD-specific strict mode.
            sendQueueRef.current = sendQueueRef.current
              .then(() => sendChunkToMediaServer(chunk.blob, {
                ...chunk.metadata,
                chunkId,
                isRetry: true
              }))
              .catch((err) => {
                console.error(`❌ [strict] Retry dispatch failed for chunk ${chunkId}:`, err);
              });
          } else {
            await sendChunkToMediaServer(chunk.blob, {
              ...chunk.metadata,
              chunkId,
              isRetry: true
            });
          }
          retryQueueRef.current = retryQueueRef.current.filter(id => id !== chunkId);
        } catch (error) {
          console.error(`❌ Retry failed for chunk ${chunkId}:`, error);
        }
      }, delay);
    }
  }, []);

  // ========================================
  // CONNECTION MONITORING FUNCTIONS
  // ========================================

  // Start heartbeat monitoring
  const startHeartbeat = useCallback(() => {
    if (!MEDIA_SERVER_CONFIG.heartbeat.enabled) return;
    
    console.log('Starting heartbeat monitoring');
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
          lastHeartbeatRef.current = Date.now();
          
          // Set timeout for pong response
          heartbeatTimeoutRef.current = setTimeout(() => {
            missedHeartbeatsRef.current++;
            console.warn(`Missed heartbeat ${missedHeartbeatsRef.current}/${MEDIA_SERVER_CONFIG.heartbeat.maxMissed}`);
            
            if (missedHeartbeatsRef.current >= MEDIA_SERVER_CONFIG.heartbeat.maxMissed) {
              console.error(' Too many missed heartbeats, connection unstable');
              setConnectionStability('unstable');
              handleConnectionLoss();
            }
          }, MEDIA_SERVER_CONFIG.heartbeat.timeout);
          
        } catch (error) {
          console.error('❌ Failed to send heartbeat:', error);
          missedHeartbeatsRef.current++;
        }
      }
    }, MEDIA_SERVER_CONFIG.heartbeat.interval);
  }, []);

  // Stop heartbeat monitoring
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    console.log(' Heartbeat monitoring stopped');
  }, []);

  // Handle pong response
  const handlePong = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    missedHeartbeatsRef.current = 0;
    setConnectionStability('stable');
    console.log(' Received pong, connection stable');
  }, []);

  // Handle connection loss with automatic reconnection
  const handleConnectionLoss = useCallback(async () => {
    console.log('🔌 Handling connection loss');
    
    if (!MEDIA_SERVER_CONFIG.reconnection.enabled) {
      console.log('🔌 Reconnection disabled, not attempting to reconnect');
      return;
    }
    
    if (reconnectAttemptsRef.current >= MEDIA_SERVER_CONFIG.reconnection.maxAttempts) {
      console.error(`❌ Max reconnection attempts (${MEDIA_SERVER_CONFIG.reconnection.maxAttempts}) reached`);
      setConnectionStability('failed');
      return;
    }
    
    reconnectAttemptsRef.current++;
    setConnectionStability('reconnecting');
    
    const delay = MEDIA_SERVER_CONFIG.reconnection.exponentialBackoff
      ? Math.min(
          MEDIA_SERVER_CONFIG.reconnection.baseDelay * Math.pow(2, reconnectAttemptsRef.current - 1),
          MEDIA_SERVER_CONFIG.reconnection.maxDelay
        )
      : MEDIA_SERVER_CONFIG.reconnection.baseDelay;
    
    console.log(`🔄 Reconnection attempt ${reconnectAttemptsRef.current}/${MEDIA_SERVER_CONFIG.reconnection.maxAttempts} in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(async () => {
      try {
        // Call the connect function using ref to avoid circular dependency
        const connectResult = await connectFunctionRef.current?.();
        if (connectResult) {
          reconnectAttemptsRef.current = 0;
          setConnectionStability('stable');
          
          // Retry any buffered chunks after reconnection
          setTimeout(() => retryFailedChunks(), 1000);
        } else {
          throw new Error('Connection failed');
        }
        
      } catch (error) {
        console.error(`❌ Reconnection attempt ${reconnectAttemptsRef.current} failed:`, error);
        handleConnectionLoss(); // Try again
      }
    }, delay);
  }, [retryFailedChunks]); // Removed connectToMediaServer to prevent circular dependency

  // Connect to media server
  const connectToMediaServer = useCallback(async () => {
    console.log('🔍 Connect called - starting media server connection process');
    
    if (!MEDIA_SERVER_CONFIG.enabled) {
      console.log('📺 Media server disabled, skipping connection');
      return false;
    }

    if (!interviewId) {
      console.error('❌ Missing interviewId for media server connection');
      return false;
    }

    // Use fallback values if candidate data not loaded yet
    const fallbackCandidateId = candidateId || 'temp-candidate';
    const fallbackUserId = userId || 'temp-user';

    try {
      setConnectionError(null);
      
      // Pre-fetch auth token for media server authentication (with fallback for sign-in-less interviews)
      let authToken = null;
      let isSignInLessInterview = false;
      
      try {
        console.log('🔐 Pre-fetching ID token for media server authentication...');
        const session = await Auth.currentSession();
        authToken = session.getIdToken().getJwtToken();
        console.log('✅ ID token pre-fetched successfully, length:', authToken.length);
      } catch (authError) {
        // Safely get error message with fallback
        const errorMessage = authError?.message || authError?.toString() || 'Unknown auth error';
        console.warn('⚠️ No authenticated user session found:', errorMessage);
        
        // Check if this is a sign-in-less interview by looking for typical indicators
        const urlParams = new URLSearchParams(window.location.search);
        const isAnonymous = urlParams.has('anonymous') || urlParams.has('guest') || 
                           window.location.pathname.includes('guest') || 
                           window.location.pathname.includes('anonymous');
        
        // For sign-in-less interviews, always proceed with anonymous authentication
        // Check for common auth error patterns or URL indicators
        const isAuthError = errorMessage.includes('No current user') || 
                           errorMessage.includes('not authenticated') ||
                           errorMessage.includes('No userPool') ||
                           authError?.code === 'NotAuthorizedException' ||
                           !authError?.message; // If message is undefined, likely auth issue
        
        if (isAnonymous || isAuthError) {
          console.log('🎭 Detected sign-in-less interview - proceeding with anonymous authentication');
          console.log('🔍 Auth error details:', { errorMessage, isAnonymous, isAuthError });
          isSignInLessInterview = true;
          authToken = 'SIGN_IN_LESS_INTERVIEW'; // Special token for anonymous users
        } else {
          console.error('❌ Failed to get authentication token for authenticated interview:', errorMessage);
          setConnectionError('Failed to get authentication token');
          return false;
        }
      }
      
      // Build WebSocket URL with parameters (including production environment)
      const wsUrl = `${MEDIA_SERVER_CONFIG.url}?interviewId=${interviewId}&candidateId=${fallbackCandidateId}&userId=${fallbackUserId}&sessionId=${sessionId}&environment=production`;
      
      console.log('🔌 Connecting to media server:', wsUrl);
      console.log('🔍 Configuration URL check:', MEDIA_SERVER_CONFIG.url);
      console.log('🔍 Auth availability check:', typeof Auth, typeof Auth.currentSession);
      
      return new Promise(async (resolve, reject) => {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = async () => {
          console.log('🔌 Connected to media server');
          console.log('🔍 Media server URL for auth check:', MEDIA_SERVER_CONFIG.url);
          setIsConnected(true);
          setConnectionStability('stable');
          reconnectAttemptsRef.current = 0;
          
          // Clear any existing reconnection timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          
          // Start heartbeat monitoring
          startHeartbeat();
          
          // Send authentication message based on interview type
          if (authToken) {
            try {
              let authMessage;
              
              if (isSignInLessInterview) {
                // Sign-in-less interview authentication
                console.log('🎭 Sending sign-in-less authentication to media server');
                authMessage = {
                  type: 'auth_sign_in_less',
                  payload: {
                    interviewId: interviewId,
                    candidateId: fallbackCandidateId,
                    userId: fallbackUserId,
                    sessionId: sessionId,
                    interviewType: 'anonymous',
                    timestamp: new Date().toISOString(),
                    guestSession: true,
                    requiresS3Upload: true,
                    anonymousToken: authToken // 'SIGN_IN_LESS_INTERVIEW'
                  }
                };
                console.log('📤 Sending sign-in-less authentication message');
              } else {
                // Regular authenticated interview with ID token.
                // Mirror the sign-in-less payload so the server persists the
                // recording the same way for both flows: include interviewId
                // (so chunks are linked to the right slot) and
                // requiresS3Upload (so finalized chunks are uploaded to S3
                // and the videoURL is written back to the InterviewEvaluation
                // row). Without these, JD-specific interviews stream chunks
                // successfully but never produce a playable video.
                console.log('🔐 Sending ID token authentication to media server');
                console.log('🔍 ID Token length:', authToken.length);
                console.log('🔍 ID Token preview:', authToken.substring(0, 50) + '...');

                authMessage = {
                  type: 'auth',
                  token: authToken, // ID token with 'aud' claim
                  payload: {
                    interviewId: interviewId,
                    candidateId: fallbackCandidateId,
                    userId: fallbackUserId,
                    sessionId: sessionId,
                    timestamp: new Date().toISOString(),
                    requiresS3Upload: true
                  }
                };
                console.log('📤 Sending ID token authentication to media server');
              }

              wsRef.current.send(JSON.stringify(authMessage));
              console.log('✅ Authentication message sent successfully');
            } catch (authError) {
              console.error('❌ Failed to send authentication to media server:', authError);
              setConnectionError('Authentication failed');
              reject(authError);
              return;
            }
          } else {
            console.error('❌ No auth token available for media server authentication');
            setConnectionError('No authentication token available');
            reject(new Error('No authentication token available'));
            return;
          }
          
          // Wait a moment for connection to stabilize before resolving
          setTimeout(() => {
            console.log('✅ Media server connection stabilized');
            resolve(true);
          }, 100);
        };

        wsRef.current.onmessage = (event) => {
          try {
            console.log('📨 Media server message received:', event.data);
            const message = JSON.parse(event.data);
            handleMessage(message);
          } catch (error) {
            console.error('❌ Error parsing media server message:', error);
            console.error('📨 Raw message data:', event.data);
          }
        };

        wsRef.current.onclose = (event) => {
          console.log('🔌 Media server connection closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          
          setIsConnected(false);
          stopHeartbeat();
          
          // Only attempt reconnection if it wasn't a clean close
          if (event.code !== 1000 && MEDIA_SERVER_CONFIG.reconnection.enabled) {
            console.log('🔄 Connection closed unexpectedly, initiating reconnection...');
            setConnectionStability('reconnecting');
            handleConnectionLoss();
          } else {
            setConnectionStability('disconnected');
          }
        };

        wsRef.current.onerror = (error) => {
          console.error('❌ Media server connection error:', error);
          const errorMessage = 'Failed to connect to media server';
          const errorObj = new Error(errorMessage);
          
          // Add additional context for debugging
          errorObj.originalError = error;
          errorObj.wsUrl = wsUrl;
          errorObj.timestamp = new Date().toISOString();
          
          setConnectionError(errorMessage);
          setIsConnected(false);
          onError?.(errorObj);
          reject(errorObj);
        };

        // Timeout after 5 seconds
        setTimeout(() => {
          if (wsRef.current?.readyState !== WebSocket.OPEN) {
            console.warn('⚠️ Media server connection timeout - server may be unavailable');
            setConnectionError('Media server connection timeout');
            reject(new Error('Connection timeout'));
          }
        }, 5000);
      });

    } catch (error) {
      console.error('❌ Failed to connect to media server:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown connection error';
      
      // Don't throw error, just log and return false for graceful degradation
      console.warn('⚠️ Media server unavailable, continuing without video recording');
      setConnectionError(`Media server unavailable: ${errorMessage}`);
      
      // Don't call onError to prevent UI error display
      return false;
    }
  }, [interviewId, sessionId, onError, startHeartbeat]);

  // Public connect function (wrapper)
  const connect = useCallback(async () => {
    return await connectToMediaServer();
  }, [connectToMediaServer]);

  // Set the connect function ref after it's defined
  useEffect(() => {
    connectFunctionRef.current = connectToMediaServer;
  }, [connectToMediaServer]);

  // Handle media server messages
  const handleMessage = useCallback((message) => {
    console.log('🔍 Processing media server message:', message.type, message);
    
    switch (message.type) {
      case 'auth_success':
        console.log('✅ Authentication successful with media server');
        console.log('🔍 Auth success details:', message.payload || message);
        break;
        
      case 'auth_sign_in_less_success':
        console.log('✅ Sign-in-less authentication successful with media server');
        console.log('🎭 Anonymous interview session established:', message.payload || message);
        break;
        
      case 'auth_error':
        const authError = message.error || message.payload?.error || 'Authentication failed - unknown reason';
        const authErrorMessage = `Media server authentication failed: ${authError}`;
        const authErrorObj = new Error(authErrorMessage);
        
        // Add debugging context
        authErrorObj.serverMessage = message;
        authErrorObj.timestamp = new Date().toISOString();
        
        console.error('❌ Authentication failed with media server:', authError);
        console.error('🔍 Full auth error message:', message);
        setConnectionError(authErrorMessage);
        onError?.(authErrorObj);
        break;
        
      case 'recording_started_ack':
        console.log('✅ Server acknowledged recording_started - ready to send chunks');
        console.log('🔍 Setting server ready state: false → true');
        setIsServerReady(true);
        isServerReadyRef.current = true;
        console.log('🔍 Server ready ref updated:', isServerReadyRef.current);
        // Proactively drain any chunks that piled up while we were waiting
        // for the server to be ready (or while we were reconnecting). The
        // drain runs through sendQueueRef so it can't race with the live
        // chunk handler.
        if (pendingChunksRef.current.length > 0) {
          const drainCount = pendingChunksRef.current.length;
          console.log(`🚚 Server ready — scheduling drain of ${drainCount} pending chunks`);
          sendQueueRef.current = sendQueueRef.current
            .then(async () => {
              if (
                wsRef.current?.readyState !== WebSocket.OPEN ||
                !isServerReadyRef.current
              ) {
                return;
              }
              const drained = pendingChunksRef.current;
              pendingChunksRef.current = [];
              for (const pending of drained) {
                if (
                  wsRef.current?.readyState !== WebSocket.OPEN ||
                  !isServerReadyRef.current
                ) {
                  pendingChunksRef.current.push(pending);
                  continue;
                }
                const pendingValidation = await validateChunk(pending.blob);
                if (!pendingValidation.valid) {
                  console.warn(`❌ Pending chunk #${pending.chunkNumber} failed validation, dropping:`, pendingValidation.reason);
                  continue;
                }
                console.log(`📤 Replaying pending chunk #${pending.chunkNumber} (${pending.elapsedSeconds}s)`);
                await sendChunkToMediaServer(pending.blob);
              }
            })
            .catch((err) => {
              console.error('❌ Pending-chunk drain failed:', err);
            });
        }
        break;
        
      case 'chunk_metadata_ack':
        const chunkId = message.payload?.chunkId || message.payload?.chunkIndex;
        if (chunkId !== undefined) {
          acknowledgeChunk(chunkId);
        }
        console.log('✅ Chunk metadata acknowledged by server:', chunkId);
        break;
        
      case 'chunk_processed':
        const processedChunkId = message.payload?.chunkId || message.payload?.chunkIndex;
        if (processedChunkId !== undefined) {
          acknowledgeChunk(processedChunkId);
        }
        const progress = message.payload?.progress || 0;
        setUploadProgress(progress);
        onProgress?.(progress);
        console.log('📊 Chunk processed:', processedChunkId, 'Progress:', progress + '%');
        break;

      case 'pong':
        handlePong();
        break;

      case 'ping':
        // Respond to server ping with pong
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const pongResponse = {
            type: 'pong',
            timestamp: Date.now(),
            originalTimestamp: message.timestamp
          };
          wsRef.current.send(JSON.stringify(pongResponse));
          console.log('💓 Responded to server ping with pong');
        }
        break;

      case 'recording_completed':
        const { s3Url, statistics } = message.payload || {};
        setRecordingUrl(s3Url);
        setIsRecording(false);
        setIsServerReady(false);
        isServerReadyRef.current = false;
        
        // Duration validation logging
        if (statistics) {
          console.log('📊 Recording statistics validation:', {
            serverDuration: statistics.duration,
            chunksProcessed: statistics.chunksProcessed,
            fileSize: statistics.fileSize,
            averageChunkSize: statistics.averageChunkSize,
            totalDataReceived: statistics.totalDataReceived,
            processingTime: statistics.processingTime,
            uploadTime: statistics.uploadTime
          });
        }
        
        console.log('✅ Recording completed:', s3Url);
        onRecordingComplete?.(s3Url, statistics);
        break;

      case 'error':
        const errorPayload = message.payload || {};
        const rawErrorMessage = errorPayload.error || message.error || 'Unknown media server error';
        const errorMessage = `Media server error: ${rawErrorMessage}`;
        
        // Handle authentication-related errors specifically
        if (rawErrorMessage.includes('Unknown message type: auth')) {
          console.error('❌ Media server does not support authentication!');
          console.error('🔍 This means your media server backend needs to be updated to handle auth messages');
          console.error('🔍 The server should accept messages like: {"type":"auth","token":"eyJ..."}');
          console.error('🔍 Current server error:', rawErrorMessage);
          
          // Don't fail the connection for auth issues, continue recording
          console.log('⚠️ Continuing without authentication - S3 upload may fail');
          break;
        }
        
        // Error logging for chunk processing issues
        if (rawErrorMessage.includes('Chunk processing failed')) {
          console.error('❌ Chunk processing failed - check media format and connection');
        }
        
        const enhancedError = new Error(errorMessage);
        enhancedError.originalPayload = errorPayload;
        enhancedError.serverMessage = message;
        enhancedError.timestamp = new Date().toISOString();
        
        console.error('❌ Media server error:', enhancedError.message);
        setConnectionError(enhancedError.message);
        setIsServerReady(false);
        isServerReadyRef.current = false;
        onError?.(enhancedError);
        break;

      default:
        // Don't log ping as unknown since we now handle it
        if (message.type !== 'ping') {
          console.log('📨 Unknown media server message:', message.type);
        }
    }
  }, [onProgress, onRecordingComplete, onError, acknowledgeChunk, handlePong]);

  // Wait for server acknowledgment
  const waitForServerReady = useCallback(() => {
    return new Promise((resolve, reject) => {
      console.log('🔍 waitForServerReady - checking current state:', isServerReadyRef.current);
      
      if (isServerReadyRef.current) {
        console.log('✅ Server already ready, proceeding immediately');
        resolve(true);
        return;
      }
      
      console.log('⏳ Server not ready yet, waiting for acknowledgment...');
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        
        console.log(`🔍 Attempt ${attempts}: Checking server ready state:`, isServerReadyRef.current);
        
        if (isServerReadyRef.current) {
          console.log('✅ Server became ready, proceeding');
          clearInterval(checkInterval);
          resolve(true);
        } else if (attempts > 50) {
          console.log('❌ Timeout waiting for server acknowledgment after', attempts, 'attempts');
          clearInterval(checkInterval);
          reject(new Error('Timeout waiting for server acknowledgment'));
        }
      }, 100);
    });
  }, []);

  // Enhanced chunk validation function with relaxed thresholds and format-specific minimums
  const validateChunk = useCallback(async (blob) => {
    // Basic validation first
    if (!blob) {
      return { valid: false, reason: 'Blob is null or undefined' };
    }
    
    if (blob.size === 0) {
      return { valid: false, reason: 'Blob is empty (0 bytes)' };
    }
    
    // Format-specific minimum sizes (VERY RELAXED)
    const minSizes = {
      'video/mp4': 32,     // MP4 can have very small fragments (reduced from 512)
      'video/webm': 100,   // WebM minimum (reduced from 800)
      'default': 20        // Default minimum (very permissive, reduced from 500)
    };
    
    const mimeTypeBase = blob.type ? blob.type.split(';')[0] : 'default';
    const minimumSize = minSizes[mimeTypeBase] || minSizes.default;
    
    if (blob.size < minimumSize) {
      // Accept ANY chunk above 10 bytes - very permissive
      if (blob.size >= 10) {
        console.warn(`⚠️ Very small but acceptable chunk: ${blob.size} bytes (min: ${minimumSize})`);
        return { 
          valid: true, 
          reason: 'Very small chunk accepted - size above 10 bytes emergency threshold',
          verySmallChunk: true 
        };
      }
      return { valid: false, reason: `Chunk too small (${blob.size} bytes, minimum ${minimumSize} for ${blob.type})` };
    }
    
    // Check for reasonable maximum size (prevent memory issues)
    const maximumSize = 50 * 1024 * 1024; // 50MB max per chunk
    if (blob.size > maximumSize) {
      return { valid: false, reason: `Blob too large (${blob.size} bytes, maximum ${maximumSize} bytes)` };
    }
    
    // Enhanced MIME type validation - now more permissive
    const validMimeTypes = [
      'video/mp4',
      'video/webm',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp9,opus',
      'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
      'video/mp4;codecs="avc1.424028,mp4a.40.2"',
      'video/mp4;codecs=avc1.420028,mp4a.40.2'
    ];
    
    if (blob.type && !validMimeTypes.some(type => blob.type.includes(type.split(';')[0]))) {
      // More permissive - warn but don't reject unknown MIME types
      console.warn(`⚠️ Unknown MIME type: ${blob.type}, but proceeding anyway`);
    }
    
    // RELAXED corruption validation - increased threshold from 10% to 50%
    const corruptionValidation = await validateChunkForCorruption(blob);
    if (!corruptionValidation.valid) {
      // Only reject if corruption is really severe (>50% instead of >10%)
      if (corruptionValidation.corruptionRatio && corruptionValidation.corruptionRatio > 0.5) {
        return {
          valid: false,
          reason: `Severe corruption detected: ${corruptionValidation.reason}`,
          corruptionDetails: corruptionValidation
        };
      } else {
        // Accept moderately corrupted chunks with warning
        console.warn(`⚠️ Moderate corruption detected but accepting chunk: ${corruptionValidation.reason}`);
        return { 
          valid: true, 
          reason: 'Chunk accepted despite moderate corruption (below 50% threshold)',
          corruptionWarning: corruptionValidation
        };
      }
    }
    
    return { 
      valid: true, 
      reason: 'Enhanced chunk validation passed (relaxed thresholds)',
      corruptionRatio: corruptionValidation.corruptionRatio
    };
  }, [validateChunkForCorruption]);

  // Send chunk to media server with enhanced corruption detection
  const sendChunkToMediaServer = useCallback(async (blob, options = {}) => {
    // Hoist the assigned sequence/chunkId so the catch block can recover it.
    // Previously the catch block read `sequenceRef.current` AFTER the post-
    // increment at the top of the try, which leaked the wrong chunkId into the
    // retry queue. That manifested as server-side "missingChunks: [N]" with
    // chunk N+1 arriving fine — assembly then broke for the rest of the take
    // (the ~3-minute / chunk-53 bug observed on slot caaa022e-32d6...).
    let assignedSequence;
    let assignedChunkId = options.chunkId;
    let bufferedForRetry = false;

    try {
      // Validation is performed by the caller (chunk handler / retry path) so
      // that ONE failure path exists per chunk. Running validateChunk again
      // here used to silently drop already-validated chunks when the second
      // pass flaked (no buffering, no retry, just `return false`) — a hidden
      // contributor to the 30-min → 3-min recording bug.

      // Double-check that server is ready for chunks using ref
      if (!isServerReadyRef.current) {
        console.warn('⚠️ Server not ready for chunks (ref check), skipping chunk');
        return false;
      }

      // Check connection stability - FIXED: Allow transmission during stable and reconnecting states
      if (connectionStability === 'failed' || wsRef.current?.readyState !== WebSocket.OPEN) {
        console.warn('⚠️ Connection not ready for transmission, buffering chunk for retry:', {
          connectionStability,
          wsReadyState: wsRef.current?.readyState,
          wsOpen: wsRef.current?.readyState === WebSocket.OPEN
        });
        return false;
      }

      // Assign sequence BEFORE the await so order reflects submission order
      // even if multiple sends are inflight.
      assignedSequence = sequenceRef.current++;
      if (assignedChunkId === undefined) assignedChunkId = assignedSequence;
      const sequence = assignedSequence;
      const chunkId = assignedChunkId;

      const buffer = await blob.arrayBuffer();
      const chunkSizeKB = Math.round(buffer.byteLength / 1024);
      const { isFinalChunk = false, isRetry = false } = options;

      // Use the recorder's chosen MIME type for every chunk. MP4 moof/mdat
      // fragments can have empty blob.type — falling back to webm mid-stream
      // mis-tags chunks and breaks server-side assembly.
      const detectedMimeType = recorderMimeTypeRef.current || blob.type || 'video/webm';

      // Create chunk metadata
      const metadataMessage = {
        type: 'chunk_metadata',
        payload: {
          size: buffer.byteLength,
          timestamp: new Date().toISOString(),
          chunkIndex: chunkId,
          chunkId: chunkId, // Include both for compatibility
          mimeType: detectedMimeType,
          isFinalChunk: isFinalChunk,
          isRetry: isRetry,
          validation: 'pre-validated by caller',
          // Add sequencing information for better server-side assembly
          sequenceNumber: sequence,
          totalExpectedChunks: isFinalChunk ? sequence + 1 : null,
          // Add checksum for data integrity verification
          dataIntegrity: {
            size: buffer.byteLength,
            firstBytes: Array.from(new Uint8Array(buffer.slice(0, 8))).map(b => b.toString(16).padStart(2, '0')).join(''),
            lastBytes: Array.from(new Uint8Array(buffer.slice(-8))).map(b => b.toString(16).padStart(2, '0')).join('')
          }
        }
      };

      // Add chunk to buffer for tracking and potential retry. Do this BEFORE
      // the wire sends so a mid-send throw still lets retryFailedChunks find
      // the blob by chunkId.
      if (!isRetry) {
        addChunkToBuffer(chunkId, blob, {
          isFinalChunk,
          mimeType: detectedMimeType,
          timestamp: new Date().toISOString()
        });
        bufferedForRetry = true;
      }

      const chunkType = isFinalChunk ? 'FINAL' : 'regular';
      const retryLabel = isRetry ? ' (RETRY)' : '';
      console.log(`📤 Sending ${chunkType} chunk_metadata ${chunkId} (${chunkSizeKB}KB)${retryLabel}:`, JSON.stringify(metadataMessage));

      // Send metadata first
      wsRef.current.send(JSON.stringify(metadataMessage));

      // Send binary chunk data immediately after metadata
      const uint8Array = new Uint8Array(buffer);
      wsRef.current.send(uint8Array);

      console.log(`📦 Sent ${chunkType} chunk ${chunkId} (${buffer.byteLength} bytes / ${chunkSizeKB}KB) to media server${retryLabel}`);

      // Set up acknowledgment timeout
      setTimeout(() => {
        if (pendingAcksRef.current.has(chunkId)) {
          console.warn(`⏰ Chunk ${chunkId} acknowledgment timeout, marking for retry`);
          retryQueueRef.current.push(chunkId);
          retryFailedChunks();
        }
      }, MEDIA_SERVER_CONFIG.buffer.acknowledgmentTimeout);

      return true;

    } catch (error) {
      console.error(`❌ Failed to send chunk to media server (assignedChunkId=${assignedChunkId}, sequence=${assignedSequence}):`, error);

      // Queue THIS chunk's id for retry, not the post-increment sequenceRef
      // value. If the failure happened before sequence assignment there's
      // nothing to retry.
      if (!options.isRetry && assignedChunkId !== undefined && bufferedForRetry) {
        retryQueueRef.current.push(assignedChunkId);
        setTimeout(() => retryFailedChunks(), MEDIA_SERVER_CONFIG.buffer.retryDelay);
      }

      return false;
    }
  }, [connectionStability, addChunkToBuffer, retryFailedChunks]);

  // Start recording (replaces your existing screen recording)
  const startRecording = useCallback(async (constraints = {}) => {
    try {
      console.log('🎥 Starting media server recording');

      // Check WebSocket connection state directly instead of relying on isConnected state
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        console.log('⏳ Media server not connected, current state:', wsRef.current?.readyState);
        throw new Error('Media server not connected');
      }

      console.log('📺 Media server connected, requesting screen share...');

      // STEP 1: Get display media stream (screen + system audio)
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
          ...constraints.video 
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000
        },
        preferCurrentTab: true
      });

      console.log('✅ Screen share granted');
      screenStreamRef.current = screenStream; // Store original screen stream for cleanup

      // STEP 2: Get microphone stream for candidate audio
      let microphoneStream;
      try {
        microphoneStream = await getMicrophoneStream();
        console.log('✅ Microphone access granted');
      } catch (error) {
        console.warn('⚠️ Microphone access failed, proceeding with screen audio only:', error.message);
        // Continue with screen recording only if microphone fails
        microphoneStream = null;
      }

      // STEP 3: Create combined audio stream (screen audio + candidate microphone)
      let finalStream;
      if (microphoneStream) {
        try {
          // Create audio mixer to combine screen and microphone audio
          const combinedAudioStream = await createAudioMixer(screenStream, microphoneStream);
          
          // Create a new MediaStream with video from screen and combined audio
          finalStream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...combinedAudioStream.getAudioTracks()
          ]);
          
          combinedStreamRef.current = finalStream;
          console.log('✅ Combined stream created with screen video + mixed audio (screen + microphone)');
          
          // Initially set microphone based on current AI talking state
          updateMicrophoneAudio(!isAITalking);
          
        } catch (error) {
          console.warn('⚠️ Audio mixing failed, using screen stream only:', error.message);
          finalStream = screenStream;
        }
      } else {
        // Use screen stream only if microphone not available
        finalStream = screenStream;
        console.log('⚠️ Using screen stream only (no microphone)');
      }

      streamRef.current = finalStream;
      console.log('✅ Final recording stream prepared with audio management');

      // STEP 1: Send recording_started message FIRST (before creating RecordRTC)
      setIsServerReady(false);
      isServerReadyRef.current = false;
      
      // Use MP4 format for better chunk compatibility (critical fix)
      const { videoBitsPerSecond: configVideoBitrate, audioBitsPerSecond: configAudioBitrate } = MEDIA_SERVER_CONFIG.mediaFormat;
      
      // Determine MP4 format to use (priority: MP4 H.264 > basic MP4 > fallback)
      const mp4MimeType = 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"';
      const fallbackMimeType = 'video/mp4';
      
      let selectedFormat = mp4MimeType;
      if (!MediaRecorder.isTypeSupported(mp4MimeType)) {
        if (MediaRecorder.isTypeSupported(fallbackMimeType)) {
          selectedFormat = fallbackMimeType;
        } else {
          selectedFormat = ''; // Let browser choose
        }
      }
      
      const startMessage = {
        type: 'recording_started',
        payload: {
          timestamp: new Date().toISOString(),
          format: selectedFormat || 'video/mp4',
          constraints: {
            videoBitsPerSecond: configVideoBitrate,
            audioBitsPerSecond: configAudioBitrate
          },
          chunkSettings: {
            chunkDuration: MEDIA_SERVER_CONFIG.chunkDuration,
            chunkMode: MEDIA_SERVER_CONFIG.chunkMode
          }
        }
      };
      
      console.log('📤 Sending recording_started message:', JSON.stringify(startMessage));
      wsRef.current.send(JSON.stringify(startMessage));
      console.log('✅ recording_started message sent to media server');

      // STEP 2: Wait for server acknowledgment
      console.log('⏳ Waiting for server acknowledgment...');
      await waitForServerReady();
      console.log('✅ Server acknowledged - ready to start RecordRTC');

      // STEP 3: Create and start RecordRTC AFTER server is ready
      console.log('🎬 Creating RecordRTC instance with MP4 format for better chunk integrity...');
      
      // Helper function to detect supported MIME types with enhanced priority
      const getSupportedMimeType = () => {
        // Use the optimal configuration from our enhanced system
        const browserSettings = getBrowserOptimalSettings();
        
        // Try browser-preferred format first
        if (MediaRecorder.isTypeSupported(browserSettings.preferredMimeType)) {
          console.log('✅ Using browser-optimized format:', browserSettings.preferredMimeType);
          return browserSettings.preferredMimeType;
        }
        
        // Fallback to the codec priority system (avc3 first if supported)
        const fallbackTypes = [
          'video/mp4; codecs="avc3.42E01E,mp4a.40.2"',
          'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
          'video/webm; codecs="vp8,opus"',
          'video/webm'
        ];
        
        for (const mimeType of fallbackTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            console.log('✅ Using fallback format:', mimeType);
            return mimeType;
          }
        }
        
        console.error('❌ No supported formats found, using basic WebM');
        return 'video/webm'; // Basic fallback
      };
      
      const selectedMimeType = getSupportedMimeType();
      const { videoBitsPerSecond: recordVideoBitrate, audioBitsPerSecond: recordAudioBitrate } = MEDIA_SERVER_CONFIG.mediaFormat;
      
      // Create minimal RecordRTC instance just for compatibility
      const recordRTCConfig = {
        type: 'video',
        // We'll override this with our direct MediaRecorder
      };
      
      const recordRTC = new RecordRTC(finalStream, recordRTCConfig);
      recordRTCRef.current = recordRTC;

            // Handle recording events with enhanced chunking monitoring  
      recordRTC.onStateChanged = function(state) {
        console.log('🎬 RecordRTC state changed (bypassed):', state);
        // We handle state through direct MediaRecorder
      };

      // STEP 4: Start recording with RecordRTC (server is now ready)
      console.log('🎬 Starting RecordRTC with optimized 2-second chunking...');
      
      // Add error handling for startRecording
      try {
        // IMPORTANT: Force MediaRecorder to use timeSlice by accessing it directly
        console.log('🎬 Starting RecordRTC with forced timeSlice...');
        
        // CRITICAL FIX: Use direct MediaRecorder with proper timeSlice instead of RecordRTC
        // RecordRTC doesn't properly pass timeSlice to MediaRecorder for MP4
        console.log('🎬 Using direct MediaRecorder for proper chunking...');
        
        // Set recording start time before creating MediaRecorder
        recordingStartTimeRef.current = Date.now();
        console.log(`⏰ Recording start time set: ${new Date(recordingStartTimeRef.current).toISOString()}`);
        
        // OPTIMAL MEDIARECORDER CONFIGURATION (enhanced codec priority and fallback system)
        console.log('🎬 Creating optimal MediaRecorder with enhanced codec selection...');
        
        // Get browser-specific optimal settings
        const browserSettings = getBrowserOptimalSettings();
        
        // Use the optimal MediaRecorder creation function
        const { recorder: mediaRecorder, config: recorderConfig } = createOptimalMediaRecorder(finalStream);

        // Capture the realized MIME type once so every chunk's metadata uses
        // the same value. `mediaRecorder.mimeType` reflects what the browser
        // actually picked, which may differ from the requested config.
        recorderMimeTypeRef.current = mediaRecorder.mimeType || recorderConfig.mimeType || null;

        console.log('✅ Optimal MediaRecorder created', {
          mimeType: mediaRecorder.mimeType || recorderConfig.mimeType,
          videoBitrate: recorderConfig.videoBitsPerSecond,
          audioBitrate: recorderConfig.audioBitsPerSecond,
          browser: navigator.userAgent.split(' ')[0],
          corruptionPrevention: 'enabled',
          relaxedValidation: 'enabled'
        });
        
        // Use browser-specific chunk interval or config default (now 3 seconds)
        const effectiveChunkInterval = browserSettings.chunkInterval || MEDIA_SERVER_CONFIG.chunkDuration;
        
        // Enhanced chunk handling with corruption detection and RELAXED empty chunk handling
        mediaRecorder.ondataavailable = async function(event) {
          const blob = event.data;
          
          // RELAXED: Accept empty chunks but log them
          if (!blob) {
            console.warn('⚠️ Received null chunk, skipping but continuing recording');
            return;
          }
          
          if (blob.size === 0) {
            // Silently handle empty chunks - they're normal for MP4 fragmented recording
            return;
          }
          
          const chunkSizeKB = Math.round(blob.size / 1024);
          const chunkSizeMB = Math.round(blob.size / (1024 * 1024) * 10) / 10;
          const timestamp = new Date().toISOString();
          const currentTime = Date.now();
          const elapsedSeconds = recordingStartTimeRef.current ? ((currentTime - recordingStartTimeRef.current) / 1000).toFixed(1) : 'unknown';
          
          // Increment chunk counter
          chunkCountRef.current++;
          const chunkNumber = chunkCountRef.current;
          
          // Log only significant chunks (every 10th chunk or large chunks)
          if (chunkNumber % 10 === 1 || chunkSizeMB > 1) {
            console.log(`📦 Chunk #${chunkNumber} at ${elapsedSeconds}s: ${chunkSizeMB}MB (${effectiveChunkInterval}ms interval)`);
          }
          
          // For very small chunks, try to accumulate them (silently handle)
          if (blob.size < 100 && chunkNumber < 3) {
            // Silently skip very small initial chunks - this is normal for MP4 recording
            return; // Skip very small initial chunks
          }
          
          // Enhanced validation with corruption detection (reduce logging)
          if (chunkNumber === 1) {
            console.log(`🔍 Starting chunk validation...`);
          }

          if (strictChunkOrderingRef.current) {
            // Strict path: enqueue immediately so arrival order is preserved,
            // then validate + send INSIDE the serial queue. Awaiting
            // validateChunk before .then() would let a faster-validating
            // chunk B overtake an earlier chunk A — the server then receives
            // out-of-order sequence numbers and assembly silently drops the
            // middle of the recording.
            sendQueueRef.current = sendQueueRef.current
              .then(async () => {
                // Drain anything that was buffered while the connection was
                // down, in arrival order, BEFORE handling this fresh chunk.
                // This preserves wall-clock order across a reconnect.
                if (
                  pendingChunksRef.current.length > 0 &&
                  wsRef.current?.readyState === WebSocket.OPEN &&
                  isServerReadyRef.current
                ) {
                  const drained = pendingChunksRef.current;
                  pendingChunksRef.current = [];
                  console.log(`🚚 [strict] Draining ${drained.length} pending chunks before chunk #${chunkNumber}`);
                  for (const pending of drained) {
                    if (
                      wsRef.current?.readyState !== WebSocket.OPEN ||
                      !isServerReadyRef.current
                    ) {
                      // Connection dropped mid-drain: re-buffer the rest in
                      // order and stop draining.
                      pendingChunksRef.current.push(pending);
                      continue;
                    }
                    const pendingValidation = await validateChunk(pending.blob);
                    if (!pendingValidation.valid) {
                      console.warn(`❌ [strict] Pending chunk #${pending.chunkNumber} failed validation:`, pendingValidation.reason);
                      continue;
                    }
                    console.log(`📤 [strict] Replaying pending chunk #${pending.chunkNumber} (${pending.elapsedSeconds}s)`);
                    await sendChunkToMediaServer(pending.blob);
                  }
                }

                // If the connection isn't usable for the CURRENT chunk,
                // buffer it for replay instead of dropping it on the floor.
                if (
                  wsRef.current?.readyState !== WebSocket.OPEN ||
                  !isServerReadyRef.current
                ) {
                  console.warn(`⏸️ [strict] Buffering chunk #${chunkNumber} for replay (ws open: ${wsRef.current?.readyState === WebSocket.OPEN}, server ready: ${isServerReadyRef.current})`);
                  // Drop oldest if pending buffer is at cap. Pending chunks
                  // haven't been assigned server sequence numbers yet, so
                  // dropping the oldest creates a time gap but not a
                  // sequence gap. This preserves the most recent activity
                  // when the connection has been down for a very long time.
                  if (pendingChunksRef.current.length >= MEDIA_SERVER_CONFIG.buffer.maxPendingChunks) {
                    const dropped = pendingChunksRef.current.shift();
                    console.error(`🚨 Pending buffer at cap (${MEDIA_SERVER_CONFIG.buffer.maxPendingChunks}). Dropping oldest pending chunk #${dropped?.chunkNumber} to make room for #${chunkNumber}.`);
                  }
                  pendingChunksRef.current.push({ blob, chunkNumber, elapsedSeconds });
                  return;
                }

                const validationResult = await validateChunk(blob);
                if (!validationResult.valid) {
                  console.warn(`❌ [strict] Chunk #${chunkNumber} failed validation:`, validationResult.reason);
                  if (validationResult.corruptionDetails) {
                    console.warn('🚨 Corruption details:', validationResult.corruptionDetails);
                  }
                  return;
                }
                console.log(`📤 [strict] Sending chunk #${chunkNumber} (${elapsedSeconds}s): ${chunkSizeKB}KB`);
                await sendChunkToMediaServer(blob);
              })
              .catch((err) => {
                console.error('❌ [strict] Queued chunk dispatch failed:', err);
              });
            return;
          }

          // Non-strict path (e.g. screening): validate up-front, then queue.
          const validationResult = await validateChunk(blob);

          if (!validationResult.valid) {
            console.warn(`❌ Chunk #${chunkNumber} failed validation - preventing transmission to avoid backend FFmpeg errors:`, validationResult.reason);

            // Log corruption details if available
            if (validationResult.corruptionDetails) {
              console.warn('🚨 Corruption details that would cause backend failure:', validationResult.corruptionDetails);
            }

            // For initial chunks, this might be normal - don't fail completely
            if (chunkNumber <= 2) {
              console.warn(`⚠️ Initial chunk #${chunkNumber} validation failed, but continuing recording...`);
              return;
            }

            return; // Skip corrupted chunks
          }

          console.log(`✅ Chunk #${chunkNumber} passed enhanced validation (corruption ratio: ${(validationResult.corruptionRatio * 100).toFixed(2)}%)`);

          // Only send if WebSocket is ready and chunk is valid
          if (wsRef.current?.readyState === WebSocket.OPEN && isServerReadyRef.current) {
            console.log(`📤 Queuing validated chunk #${chunkNumber} (${elapsedSeconds}s): ${chunkSizeKB}KB to media server`);
            // Serialize dispatch: each chunk's metadata + binary pair must
            // complete on the wire before the next chunk starts. Without
            // this, concurrent async sends interleave and the server sees
            // binary frames without preceding metadata ("No chunk metadata
            // prepared") plus out-of-order sequences.
            sendQueueRef.current = sendQueueRef.current
              .then(() => sendChunkToMediaServer(blob))
              .catch((err) => {
                console.error('❌ Queued chunk dispatch failed:', err);
              });
          } else if (!isServerReadyRef.current) {
            console.warn(`⚠️ Skipping chunk #${chunkNumber} at ${elapsedSeconds}s - server not ready`);
          } else if (wsRef.current?.readyState !== WebSocket.OPEN) {
            console.warn(`⚠️ Skipping chunk #${chunkNumber} at ${elapsedSeconds}s - WebSocket not open`);
          }
        };
        
        // Override RecordRTC's internal recorder with our enhanced one
        recordRTC.getInternalRecorder = () => mediaRecorder;
        
        // Enhanced error handling for MediaRecorder issues
        mediaRecorder.onerror = function(event) {
          console.error('❌ Enhanced MediaRecorder error:', event.error);
          
          // Attempt recovery based on error type
          if (mediaRecorder.state === 'recording') {
            console.log('🔄 Attempting to stop corrupted recording for recovery');
            try {
              mediaRecorder.stop();
            } catch (stopError) {
              console.error('❌ Failed to stop recorder during error recovery:', stopError);
            }
          }
          
          // Notify server of recording failure with error details
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            const errorMessage = {
              type: 'recording_error',
              payload: {
                error: event.error.name,
                message: event.error.message,
                timestamp: new Date().toISOString(),
                mimeType: mediaRecorder.mimeType,
                state: mediaRecorder.state,
                browserUA: navigator.userAgent
              }
            };
            wsRef.current.send(JSON.stringify(errorMessage));
          }
          
          // Call user error handler
          onError?.(new Error(`MediaRecorder error: ${event.error.message}`));
        };
        
        mediaRecorder.onstart = function() {
          console.log('✅ Enhanced MediaRecorder started successfully', {
            mimeType: mediaRecorder.mimeType,
            state: mediaRecorder.state,
            timestamp: new Date().toISOString(),
            corruptionPrevention: 'active'
          });
        };
        
        mediaRecorder.onstop = function() {
          console.log('✅ Enhanced MediaRecorder stopped successfully', {
            finalState: mediaRecorder.state,
            timestamp: new Date().toISOString()
          });
        };
        
        // ENHANCED FIX: Start with browser-optimized timeSlice for frequent, smaller chunks
        mediaRecorder.start(effectiveChunkInterval); // Use 1-second chunks for frequent transmission
        console.log(`✅ Optimal MediaRecorder started with ${effectiveChunkInterval}ms chunks (frequent transmission)`);
        
        // Set up interval to request additional chunks using browser-optimized interval
        const chunkInterval = setInterval(() => {
          if (mediaRecorder.state === 'recording') {
            console.log(`⏰ Requesting chunk data at ${((Date.now() - recordingStartTimeRef.current) / 1000).toFixed(1)}s`);
            mediaRecorder.requestData(); // This generates a complete chunk
          } else {
            console.log('⏰ Clearing chunk interval - MediaRecorder not recording');
            clearInterval(chunkInterval);
          }
        }, effectiveChunkInterval); // Use browser-optimized interval
        
        // Store interval reference for cleanup
        recordRTC.chunkInterval = chunkInterval;
        
        console.log(`📋 Using timeSlice(${effectiveChunkInterval}ms) + requestData() for frequent chunking (browser-optimized)`);
        console.log('🛡️ Enhanced corruption detection enabled with relaxed 50% threshold - prevents severe corruption only');
        
        // Verify recording actually started after a short delay
        setTimeout(() => {
          const mediaRecorder = recordRTC.getInternalRecorder();
          if (!mediaRecorder || mediaRecorder.state !== 'recording') {
            console.error('❌ Enhanced MediaRecorder failed to start properly. State:', mediaRecorder?.state || 'null');
            // Clear interval if recorder failed
            if (recordRTC.chunkInterval) {
              clearInterval(recordRTC.chunkInterval);
              recordRTC.chunkInterval = null;
            }
            throw new Error('Enhanced MediaRecorder failed to start recording');
          } else {
            console.log('✅ Enhanced MediaRecorder verified to be recording', {
              state: mediaRecorder.state,
              mimeType: mediaRecorder.mimeType,
              chunkInterval: effectiveChunkInterval,
              corruptionPrevention: 'active'
            });
            
            console.log(`📋 Using enhanced requestData() every ${effectiveChunkInterval}ms with corruption detection`);
            console.log(`🎯 For a 43.58-second recording, expecting ~${Math.ceil(43580 / effectiveChunkInterval)} validated chunks`);
            
            // Set recording state and reset counters
            setIsRecording(true);
            setUploadProgress(0);
            chunkCountRef.current = 0; // Reset chunk counter
            console.log('🎬 Enhanced recording state set to active with corruption prevention');
          }
        }, 1000);
        
      } catch (startError) {
        console.error('❌ Error starting RecordRTC:', startError);
        throw new Error(`Failed to start RecordRTC: ${startError.message}`);
      }
      
      console.log('✅ Media server recording started');
      return true;

    } catch (error) {
      console.error('❌ Failed to start media server recording:', error);
      
      // Provide more specific error messages
      if (error.name === 'NotAllowedError') {
        const specificError = new Error('Screen sharing permission denied. Please allow screen sharing to start recording.');
        onError?.(specificError);
        throw specificError;
      } else if (error.name === 'NotFoundError') {
        const specificError = new Error('No screen sharing source available. Please ensure you have a screen or window to share.');
        onError?.(specificError);
        throw specificError;
      } else if (error.message.includes('Media server not connected')) {
        const specificError = new Error('Media server connection lost. Please try again.');
        onError?.(specificError);
        throw specificError;
      }
      
      onError?.(error);
      throw error;
    }
  }, [waitForServerReady, sendChunkToMediaServer, getMicrophoneStream, createAudioMixer, updateMicrophoneAudio, isAITalking]);

  // Stop recording - CORRECTED LOGIC
  const stopRecording = useCallback(() => {
    console.log('🛑 stopRecording called - isRecording:', isRecording, 'recordRTC exists:', !!recordRTCRef.current);

    // Guard clause: if there's no recorder, there's nothing to stop.
    if (!recordRTCRef.current) {
      console.warn('⚠️ stopRecording called but no active recorder found.');
      return;
    }
    
    console.log('🛑 Stopping recording and preventing duplicate chunks...');

    try {
      // STEP 1: Clear chunk request interval FIRST to prevent new chunks
      if (recordRTCRef.current.chunkInterval) {
        console.log('⏰ Clearing chunk request interval to prevent duplicate chunks');
        clearInterval(recordRTCRef.current.chunkInterval);
        recordRTCRef.current.chunkInterval = null;
      }

      // STEP 2: Stop the direct MediaRecorder (will automatically generate final chunk)
      const mediaRecorder = recordRTCRef.current.getInternalRecorder();
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        console.log('🛑 Stopping MediaRecorder - will automatically generate final chunk...');
        
        // Set up ONE-TIME final chunk handler for automatic final chunk
        const handleFinalChunk = (event) => {
          console.log('🛑 MediaRecorder stopped. Processing automatic final chunk...');
          
          // Remove this listener immediately to prevent any duplicates
          mediaRecorder.removeEventListener('dataavailable', handleFinalChunk);
          
          const finalBlob = event.data;
          const minimumChunkSize = 1024; // 1KB minimum

          // STEP 3 + 4: Queue the final chunk and the completion message
          // through the SAME serial queue as regular chunks. This guarantees
          // (a) the final chunk arrives after every queued regular chunk,
          // and (b) `recording_stopped` arrives strictly AFTER the final
          // chunk's metadata + binary — no more racing setTimeouts that let
          // the server finalize early and drop trailing data.
          if (finalBlob && finalBlob.size > minimumChunkSize) {
            console.log(`🎯 Final chunk captured: ${Math.round(finalBlob.size / 1024)}KB. Queuing as final...`);
            // Validate the final chunk in the serial queue so it can't be
            // overtaken by anything still pending. sendChunkToMediaServer no
            // longer self-validates, so callers must validate before dispatch.
            sendQueueRef.current = sendQueueRef.current
              .then(async () => {
                const finalValidation = await validateChunk(finalBlob);
                if (!finalValidation.valid) {
                  console.warn('❌ Final chunk failed validation, skipping:', finalValidation.reason);
                  return;
                }
                await sendChunkToMediaServer(finalBlob, { isFinalChunk: true });
              })
              .catch((err) => console.error('❌ Final chunk dispatch failed:', err));
          } else {
            console.log('📋 Final chunk too small or empty, skipping.');
          }

          sendQueueRef.current = sendQueueRef.current
            .then(() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                const completionMessage = {
                  type: 'recording_stopped',
                  payload: {
                    timestamp: new Date().toISOString(),
                    totalChunks: sequenceRef.current,
                    reason: 'manual_stop'
                  }
                };
                console.log('📤 All chunks flushed. Sending recording_stopped to media server.');
                wsRef.current.send(JSON.stringify(completionMessage));
              } else {
                console.warn('⚠️ Cannot send recording_stopped message - WebSocket not open');
              }
            })
            .catch((err) => console.error('❌ recording_stopped dispatch failed:', err));
        };
        
        // Disable the regular chunk handler before stop() so the final
        // dataavailable event is consumed ONLY by handleFinalChunk. Without
        // this, both handlers fire on the same final blob — the property
        // handler queues it as a regular chunk and handleFinalChunk queues
        // it again as final, causing the server's "Chunk processing failed"
        // duplicate-chunk error.
        mediaRecorder.ondataavailable = null;

        // Add the one-time event listener for automatic final chunk
        mediaRecorder.addEventListener('dataavailable', handleFinalChunk);

        // Stop the MediaRecorder (will automatically trigger final ondataavailable)
        mediaRecorder.stop();
        console.log('✅ MediaRecorder.stop() called - awaiting automatic final chunk');
      } else {
        console.log('ℹ️ No active MediaRecorder found or already stopped');

        // Queue completion behind any in-flight chunks so it can't outrace them.
        sendQueueRef.current = sendQueueRef.current
          .then(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              const completionMessage = {
                type: 'recording_stopped',
                payload: {
                  timestamp: new Date().toISOString(),
                  totalChunks: sequenceRef.current,
                  reason: 'manual_stop'
                }
              };
              console.log('📤 Sending recording_stopped message to media server.');
              wsRef.current.send(JSON.stringify(completionMessage));
            }
          })
          .catch((err) => console.error('❌ recording_stopped dispatch failed:', err));
      }
    } catch (error) {
      console.error('❌ Error during the stopRecording process:', error);
    }

    // Stop the browser's media stream tracks (including screen recording)
    if (streamRef.current) {
      console.log('🛑 Stopping all media stream tracks (screen + audio)...');
      streamRef.current.getTracks().forEach(track => {
        console.log(`🛑 Stopping ${track.kind} track:`, track.label);
        track.stop();
      });
      streamRef.current = null;
      console.log('✅ All media tracks stopped - screen recording indicator should disappear');
    }

    // Stop original screen stream if it exists separately
    if (screenStreamRef && screenStreamRef.current) {
      console.log('🛑 Stopping original screen stream...');
      screenStreamRef.current.getTracks().forEach(track => {
        console.log(`🛑 Stopping original screen ${track.kind} track:`, track.label);
        track.stop();
      });
      screenStreamRef.current = null;
    }

    // Clean up audio streams and context
    cleanupAudioStreams();

    setIsRecording(false);
    console.log('🛑 Media server recording process has been gracefully stopped.');

  }, [isRecording, sendChunkToMediaServer, cleanupAudioStreams]);

  // Best-effort SYNCHRONOUS interruption signal. Use this from `pagehide`,
  // `beforeunload`, or component-unmount paths where the async stop pipeline
  // can't run to completion before the browser kills the WebSocket.
  //
  // It does the minimum needed for the server to finalize whatever data it
  // already has:
  //   1. Sends `recording_stopped` directly (bypassing the async queue) so
  //      the server is unblocked from waiting on a final chunk that will
  //      never arrive.
  //   2. Calls `mediaRecorder.requestData()` + `stop()` so the browser
  //      flushes any buffered data on its way out — best effort.
  //
  // Not a replacement for `stopRecording()`; only used when graceful stop
  // isn't possible.
  const signalInterruption = useCallback((reason = 'interrupted') => {
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const completionMessage = {
          type: 'recording_stopped',
          payload: {
            timestamp: new Date().toISOString(),
            totalChunks: sequenceRef.current,
            reason,
            interrupted: true
          }
        };
        wsRef.current.send(JSON.stringify(completionMessage));
        console.log(`📤 Sent synchronous recording_stopped (reason=${reason})`);
      }
    } catch (err) {
      console.error('❌ Failed to send interruption signal:', err);
    }

    try {
      const mediaRecorder = recordRTCRef.current?.getInternalRecorder?.();
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.requestData();
        mediaRecorder.stop();
      }
    } catch (err) {
      console.error('❌ Failed to flush MediaRecorder on interruption:', err);
    }
  }, []);

  // Disconnect from media server
  const disconnect = useCallback(() => {
    console.log('🔌 disconnect called - cleaning up media server connection');
    
    // Stop heartbeat monitoring
    stopHeartbeat();
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      console.log('🔄 Clearing reconnection timeout');
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Clear any active chunk intervals
    if (recordRTCRef.current?.chunkInterval) {
      console.log('⏰ Clearing chunk request interval during disconnect');
      clearInterval(recordRTCRef.current.chunkInterval);
      recordRTCRef.current.chunkInterval = null;
    }
    
    if (wsRef.current) {
      console.log('🔌 Closing WebSocket connection...');
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }
    
    // Clear buffer and pending acknowledgments
    console.log('🗑️ Clearing chunk buffer and pending acknowledgments');
    chunkBufferRef.current.clear();
    pendingAcksRef.current.clear();
    retryQueueRef.current = [];
    pendingChunksRef.current = [];
    
    // Clean up audio streams and context
    cleanupAudioStreams();
    
    // Reset all states
    setIsConnected(false);
    setIsServerReady(false);
    setConnectionStability('disconnected');
    isServerReadyRef.current = false;
    setIsRecording(false);
    setUploadProgress(0);
    setRecordingUrl(null);
    setConnectionError(null);
    
    // Reset all refs
    sequenceRef.current = 0;
    recordingStartTimeRef.current = null;
    chunkCountRef.current = 0;
    missedHeartbeatsRef.current = 0;
    reconnectAttemptsRef.current = 0;
    lastHeartbeatRef.current = null;
    connectFunctionRef.current = null;
    sendQueueRef.current = Promise.resolve();
    recorderMimeTypeRef.current = null;
    
    console.log('✅ Media server connection and all monitoring systems cleaned up completely');
  }, [cleanupAudioStreams, stopHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    // State
    isConnected,
    isRecording,
    connectionError,
    uploadProgress,
    recordingUrl,
    connectionStability,
    isEnabled: MEDIA_SERVER_CONFIG.enabled,

    // Buffer and monitoring stats
    bufferedChunks: chunkBufferRef.current.size,
    pendingAcknowledgments: pendingAcksRef.current.size,
    retryQueueSize: retryQueueRef.current.length,
    missedHeartbeats: missedHeartbeatsRef.current,
    reconnectAttempts: reconnectAttemptsRef.current,

    // Audio stream management stats
    hasMicrophoneStream: !!microphoneStreamRef.current,
    hasAudioMixer: !!audioMixerRef.current,
    isMicrophoneEnabled: !isAITalking, // Shows current microphone state

    // Actions
    connect,
    startRecording,
    stopRecording,
    signalInterruption,
    disconnect,
    
    // Advanced actions
    retryFailedChunks,
    updateMicrophoneAudio, // Expose for manual control if needed
    
    // Configuration
    config: MEDIA_SERVER_CONFIG
  };
};